import api from '@/lib/services/general.service'

// Mirrors acc-account-sync-srv's FirstSyncStatusEnum wire values (plain ints — no
// JsonStringEnumConverter anywhere on that response chain).
export const FIRST_SYNC_STATUS = {
  NOT_STARTED: 0,
  IN_PROGRESS: 1,
  COMPLETED: 2,
  FAILED: 3,
  // A reconnect of a mailbox we've already synced before (disconnect → connect again) — the
  // connect-time metadata sync is for brand-new connections only, so the backend skipped it.
  SKIPPED: 4,
};

const initialState = () => ({
  allEmailSync: {
    totalEmails: 0,
    estimetedTime: 360,
    isLoading: false,
  },
  gmailSyncStatus: null,
  gmailSyncNotificationDismissed: false,
  loadingPercentage: "0%",
  elapsedTime: 0,
  syncTimer: null,

  // Connect-time mailbox metadata sync (the connect-flow progress bar) — subject/labels/preview
  // for every email, no bodies. Separate from allEmailSync above, which is the full-mailbox "Pro"
  // import (kept as its own concept even though this sync now also covers the whole mailbox).
  firstTimeSync: {
    status: FIRST_SYNC_STATUS.NOT_STARTED,
    totalCount: 0,
    syncedCount: 0,
  },
  firstTimeSyncTimer: null,
});

export const state = initialState();

export const getters = {
  allEmailSync: s => s.allEmailSync,
  syncAllEmailInProgress: s => s.allEmailSync.isLoading,
  loadingPercentage: s => s.loadingPercentage,
  elapsedTime: s => s.elapsedTime,
  gmailSyncStatus: s => s.gmailSyncStatus,
  gmailSyncNotificationDismissed: s => s.gmailSyncNotificationDismissed,
  firstTimeSync: s => s.firstTimeSync,
  firstTimeSyncInProgress: s => s.firstTimeSync.status === FIRST_SYNC_STATUS.IN_PROGRESS,
  // Whether a timer is ACTIVELY polling right now, as opposed to firstTimeSyncInProgress (the
  // last known status, which now survives a component unmount — see stopFirstTimeSyncPolling).
  // Two different questions: "is the sync still running server-side" vs. "is something in this
  // browser tab currently asking about it."
  firstTimeSyncPolling: s => !!s.firstTimeSyncTimer,
};
const calculateLoadingPercentage = (elapsedTime, estimatedTime) => {
  if (estimatedTime === 0) return "0%";
  const percentage = Math.min((elapsedTime / estimatedTime) * 100, 100);
  return `${percentage.toFixed(0)}%`;
};

const waitForRequiredResponses = async () => {
  let contactSuccess = false;

  while (!contactSuccess) {
    try {
      if (!contactSuccess) {
        const contactResponse = await api.get(`timeline/count`, {});
        if (contactResponse?.status === 200) {
          contactSuccess = true;
        }
      }

      if (!contactSuccess) {
        // console.log("🔄 Waiting before retrying...");
        await new Promise(resolve => setTimeout(resolve, 60000)); // Wait 5 seconds before checking again
      }
    } catch (error) {
      console.error("⚠️ Error while fetching required responses:", error);
      await new Promise(resolve => setTimeout(resolve, 60000)); // Wait before retrying if there's an error
    }
  }
};
export const actions = {

  async fetchGmailSyncStatus({ commit, rootGetters }) {
    if (rootGetters['user/isPro']) return;
    const response = await api.get('sync/gmail-sync-status', {});
    if (response) {
      commit('SET_GMAIL_SYNC_STATUS', response.data);
    }
  },

  dismissGmailSyncNotification({ commit }) {
    commit('DISMISS_GMAIL_SYNC_NOTIFICATION');
  },


  // Pull the WHOLE mailbox down — what Pro buys (busy-my-web#502). Nothing called this: `/pro`
  // granted the flag and stopped, the Stripe success page did nothing at all, and the endpoint was
  // reachable only by hand. Post once, keep the job it answers with (it carries the totals the
  // banner draws), then hand over to firstSync for the progress poll.
  async startAllEmails({ dispatch, commit, getters }) {
    // One sync per user; the server says the same, but a second POST here would also reset the
    // banner's timer under a sync that is already halfway through.
    if (getters.syncAllEmailInProgress) return;

    try {
      const response = await api.insert('sync/all-emails', {});
      if (response?.data) {
        commit('SET', response.data);
        const estimatedTime = response.data.estimetedTime;
        if (estimatedTime > 0) commit('START_TIMER', estimatedTime);
      }
    } catch (error) {
      console.error('Failed to start the full mailbox sync:', error);
      return;
    }

    // The poll owns everything from here: progress, and the refresh of every list when it lands.
    return dispatch('firstSync');
  },

  async firstSync({ commit, dispatch, getters }) {
    const response = await api.get('sync/allEmailSync', {});
    if (response) {
      commit('SET', response.data);
      const estimatedTime = response.data.estimetedTime;
      if (estimatedTime > 0) {
        commit('START_TIMER', estimatedTime);
      }
    }
    // Nothing is running — don't leave a timer ticking against a sync that is not happening. This
    // matters because firstSync is now dispatched on every boot for a Pro user (auth.service.js),
    // to pick the banner back up after a reload mid-sync; on all the other boots it must cost
    // nothing and leave no interval behind (busy-my-web#502).
    if (!response?.data?.isLoading) {
      commit('STOP_TIMER');
      return;
    }
    const interval = setInterval(async () => {
      const syncState = getters.syncAllEmailInProgress;
      if (syncState) {
        const response = await api.get('sync/allEmailSync', {});
        if (response.data.isLoading) {
          commit("SET", response.data)
        }
        else {
          clearInterval(interval); // Stop checking sync
          await waitForRequiredResponses();
          commit("STOP_TIMER");
          commit("SET", response.data);

          // Silent refresh — re-fetch all data without reloading the page
          dispatch('timeline/fetchTimelineCount', null, { root: true })
          dispatch('timeline/fetchContactsFilter', null, { root: true })
          dispatch('contacts/fetchAllContacts', null, { root: true })
          dispatch('email/fetchEmails', null, { root: true })
          dispatch('spamEmail/fetchEmails', null, { root: true })
          dispatch('draftEmail/fetchEmails', null, { root: true })
          dispatch('note/fetchNotesAndCounts', null, { root: true })
        }
      }
      else {
        clearInterval(interval);
      }
    }, 35000);
  },

  // Connect-time mailbox metadata sync started by the Google-connect callback. Polled fast (it's
  // metadata only, so even a large mailbox finishes well under the full-mailbox body/attachment
  // "Pro" job's minutes/hours) right after the web lands back with ?connected=google.
  async pollFirstTimeSyncStatus({ commit, getters }) {
    // Guards re-entrant calls (e.g. a remount while a timer from this same tab is still ticking),
    // NOT "was already in progress" — that's the status, which now survives an unmount, and using
    // it here would block ever resuming a poll after one (2026-09-27).
    if (getters.firstTimeSyncPolling) return;

    const poll = async () => {
      let response;
      try {
        response = await api.get('sync/firsttime/status', {});
      } catch (error) {
        console.error('Failed to poll the first-time sync status:', error);
        return;
      }
      if (!response?.data) return;

      commit('SET_FIRST_TIME_SYNC', response.data);

      if (response.data.status === FIRST_SYNC_STATUS.IN_PROGRESS) return;

      commit('STOP_FIRST_TIME_SYNC_TIMER');
    };

    await poll();
    if (!getters.firstTimeSyncInProgress) return;

    commit('START_FIRST_TIME_SYNC_TIMER', poll);
  },

  resetFirstTimeSync({ commit }) {
    commit('STOP_FIRST_TIME_SYNC_TIMER');
    commit('SET_FIRST_TIME_SYNC', { status: FIRST_SYNC_STATUS.NOT_STARTED, totalCount: 0, syncedCount: 0 });
  },

  // Leaving the Integrations page mid-poll must not leave a background interval ticking forever —
  // but unlike resetFirstTimeSync, this must NOT wipe the counts: the sync is still running
  // server-side regardless of what page the user is on, and switching away and back used to lose
  // all visibility into it (2026-09-27). Stopping only the local timer lets a remount show the
  // last known progress immediately (see Integrations.vue's immediate watcher) while a fresh poll
  // catches it up.
  stopFirstTimeSyncPolling({ commit }) {
    commit('STOP_FIRST_TIME_SYNC_TIMER');
  },
};

export const mutations = {
  SET(s, syncResponse) {
    s.allEmailSync = syncResponse;
  },
  SET_GMAIL_SYNC_STATUS(s, status) {
    s.gmailSyncStatus = status;
  },
  DISMISS_GMAIL_SYNC_NOTIFICATION(s) {
    s.gmailSyncNotificationDismissed = true;
  },
  START_TIMER(s, estimatedTime) {
    if (s.syncTimer) clearInterval(s.syncTimer); // Prevent duplicate timers

    s.syncTimer = setInterval(() => {
      if (s.elapsedTime < estimatedTime) {
        s.elapsedTime += 1;
        s.loadingPercentage = calculateLoadingPercentage(s.elapsedTime, estimatedTime);
      } else {
        clearInterval(s.syncTimer);
        s.syncTimer = null;
        s.loadingPercentage = "100%";
      }
    }, 1000);
  },
  STOP_TIMER(s) {
    if (s.syncTimer) {
      clearInterval(s.syncTimer);
      s.syncTimer = null;
    }
  },
  SET_FIRST_TIME_SYNC(s, status) {
    s.firstTimeSync = status;
  },
  START_FIRST_TIME_SYNC_TIMER(s, poll) {
    if (s.firstTimeSyncTimer) clearInterval(s.firstTimeSyncTimer);
    s.firstTimeSyncTimer = setInterval(poll, 1500);
  },
  STOP_FIRST_TIME_SYNC_TIMER(s) {
    if (s.firstTimeSyncTimer) {
      clearInterval(s.firstTimeSyncTimer);
      s.firstTimeSyncTimer = null;
    }
  },
}
export default {
  state,
  getters,
  actions,
  mutations
};